/**
 * AI Interview Service
 * 
 * This service handles AI-powered interview analysis using OpenAI GPT-4 via Firebase Functions.
 */

import { auth } from '../firebase/config';

// Get Firebase Functions URL
const FUNCTIONS_URL = 'https://us-central1-mahstry.cloudfunctions.net';

/**
 * Generate comprehensive interview report using AI
 */
export const generateInterviewReport = async (candidateData, allAnswers, jobRequirements) => {
  console.log('📊 Generating interview report...');
  
  try {
    // Get auth token
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    const token = await user.getIdToken();

    const response = await fetch(`${FUNCTIONS_URL}/openaiInterviewAnalysis`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        candidateData,
        allAnswers,
        jobRequirements
      })
    });

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Failed to analyze interview');
    }

    console.log('✅ Interview analysis completed');
    return result.analysis;

  } catch (error) {
    console.error('❌ Error calling OpenAI:', error);
    console.error('Error details:', error.message);
    
    return generateMockReport(candidateData, allAnswers, jobRequirements);
  }
};

/**
 * Mock report fallback
 */
function generateMockReport(candidateData, allAnswers, jobRequirements) {
  console.log('📝 Generating mock report (OpenAI not available)');
  
  const questionRatings = allAnswers.map((answer, index) => ({
    questionIndex: index,
    question: answer.question,
    rating: Math.floor(Math.random() * 25) + 60,
    strengths: ['Clear communication', 'Relevant experience'],
    improvements: ['More specific examples needed'],
    transcript: 'Video response provided',
    timeSpent: answer.timeSpent || 0
  }));

  const overallRating = Math.floor(
    questionRatings.reduce((sum, q) => sum + q.rating, 0) / questionRatings.length
  );

  return {
    overallRating,
    questionRatings,
    technicalSkills: {
      rating: Math.floor(Math.random() * 20) + 65,
      feedback: '⚠️ MOCK: OpenAI service not configured on backend.'
    },
    communication: {
      rating: Math.floor(Math.random() * 20) + 65,
      feedback: '⚠️ MOCK: Contact admin to configure OpenAI API key in Firebase Functions.'
    },
    problemSolving: {
      rating: Math.floor(Math.random() * 20) + 60,
      feedback: '⚠️ MOCK: Check browser console for OpenAI initialization status.'
    },
    cultureFit: {
      rating: Math.floor(Math.random() * 20) + 60,
      feedback: '⚠️ MOCK: Real AI analysis will provide detailed feedback.'
    },
    strengths: [
      '⚠️ MOCK ANALYSIS - OpenAI service not configured',
      'Strong technical background',
      'Good communication skills'
    ],
    areasForImprovement: [
      '⚠️ Contact admin to enable AI analysis',
      'Could demonstrate more examples'
    ],
    recommendation: 'Consider',
    summary: `⚠️ MOCK ANALYSIS: OpenAI service not configured on backend. ${candidateData.name} demonstrates adequate qualifications.`,
    detailedFeedback: 'This is mock analysis. Contact your administrator to configure OpenAI API key in Firebase Functions.'
  };
}

export default {
  generateInterviewReport
};
